import { useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import type { WsEvent } from '../lib/ws-client';
import { Icon } from './Icon';

export type HumanActionKind = 'select' | 'fill' | 'approve' | 'upload' | 'inject' | 'takeover';
export type HumanActionStatus = 'pending' | 'completed' | 'timed_out' | 'canceled';

export interface HumanActionCardModel {
  action_id: string;
  session_id?: string;
  kind: HumanActionKind;
  status: HumanActionStatus;
  payload: Record<string, unknown>;
  resolution?: Record<string, unknown>;
  requested_audit_id: number;
  resolved_audit_id?: number;
  ts: number;
  timeout_at?: number | null;
}

export interface HumanActionPanelProps {
  sessionId: string;
  backendUrl?: string;
  authToken?: string | null;
  events: WsEvent[];
  selectedNodeId?: string | null;
  fetchImpl?: typeof fetch;
}

const HUMAN_KINDS = new Set<HumanActionKind>([
  'select',
  'fill',
  'approve',
  'upload',
  'inject',
  'takeover',
]);

const HUMAN_STATUSES = new Set<HumanActionStatus>([
  'pending',
  'completed',
  'timed_out',
  'canceled',
]);

const KIND_LABEL: Record<HumanActionKind, string> = {
  select: '选择',
  fill: '填空',
  approve: '批准',
  upload: '上传',
  inject: '插话',
  takeover: '接管',
};

const KIND_ICON: Record<HumanActionKind, string> = {
  select: 'check',
  fill: 'note',
  approve: 'check',
  upload: 'upload',
  inject: 'send',
  takeover: 'stop',
};

export function eventsToHumanActions(events: WsEvent[]): HumanActionCardModel[] {
  const byId = new Map<string, HumanActionCardModel>();
  const sorted = [...events]
    .filter((e) => typeof e.audit_id === 'number' && Number.isSafeInteger(e.audit_id) && e.audit_id > 0)
    .sort((a, b) => (a.audit_id ?? 0) - (b.audit_id ?? 0));

  for (const evt of sorted) {
    const payload = objectValue(evt.payload);
    const actionId = textValue(payload.action_id);
    if (!actionId) continue;

    if (evt.kind === 'human.requested') {
      const kind = humanKind(payload.kind);
      if (!kind) continue;
      byId.set(actionId, {
        action_id: actionId,
        session_id: textValue(payload.session_id) ?? undefined,
        kind,
        status: humanStatus(payload.status) ?? 'pending',
        payload: objectValue(payload.payload),
        requested_audit_id: evt.audit_id!,
        ts: numberValue(evt.ts) ?? 0,
        timeout_at: numberValue(payload.timeout_at),
      });
      continue;
    }

    if (evt.kind === 'tool.requested' && !byId.has(actionId)) {
      byId.set(actionId, {
        action_id: actionId,
        session_id: textValue(payload.session_id) ?? undefined,
        kind: 'approve',
        status: 'pending',
        payload: {
          tool_name: payload.tool_name,
          agent_id: payload.agent_id,
          source: payload.source,
          reason: payload.reason,
        },
        requested_audit_id: evt.audit_id!,
        ts: numberValue(evt.ts) ?? 0,
        timeout_at: null,
      });
      continue;
    }

    if (evt.kind === 'human.resolved') {
      const current = byId.get(actionId);
      if (!current) continue;
      current.status = humanStatus(payload.status) ?? 'completed';
      current.resolution = objectValue(payload.resolution);
      current.resolved_audit_id = evt.audit_id;
    }
  }

  return [...byId.values()].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    return b.requested_audit_id - a.requested_audit_id;
  });
}

export async function createHumanActionRequest(opts: {
  backendUrl: string;
  authToken: string;
  sessionId: string;
  kind: HumanActionKind;
  payload: Record<string, unknown>;
  fetchImpl?: typeof fetch;
}): Promise<{ ok: true; action_id: string } | { ok: false; error: string }> {
  const fetcher = opts.fetchImpl ?? fetch;
  const resp = await fetcher(`${opts.backendUrl.replace(/\/$/, '')}/api/human-actions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${opts.authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      session_id: opts.sessionId,
      kind: opts.kind,
      payload: opts.payload,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) return { ok: false, error: String(data?.error ?? `HTTP ${resp.status}`) };
  return { ok: true, action_id: String(data.action_id) };
}

export async function resolveHumanActionRequest(opts: {
  backendUrl: string;
  authToken: string;
  actionId: string;
  resolution: Record<string, unknown>;
  status?: Exclude<HumanActionStatus, 'pending'>;
  fetchImpl?: typeof fetch;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const fetcher = opts.fetchImpl ?? fetch;
  const resp = await fetcher(
    `${opts.backendUrl.replace(/\/$/, '')}/api/human-actions/${encodeURIComponent(opts.actionId)}/resolve`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        resolution: opts.resolution,
        ...(opts.status ? { status: opts.status } : {}),
      }),
    },
  );
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    return { ok: false, error: String(data?.error ?? `HTTP ${resp.status}`) };
  }
  return { ok: true };
}

export function HumanActionPanel({
  sessionId,
  backendUrl = '',
  authToken,
  events,
  selectedNodeId,
  fetchImpl,
}: HumanActionPanelProps): JSX.Element | null {
  const actions = useMemo(() => eventsToHumanActions(events), [events]);
  const pending = actions.filter((a) => a.status === 'pending');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState<Record<string, boolean>>({});
  const [quickInject, setQuickInject] = useState('');

  if (pending.length === 0 && !authToken) return null;

  const canSubmit = Boolean(authToken);

  function draft(id: string): string {
    return drafts[id] ?? '';
  }

  function setDraft(id: string, value: string): void {
    setDrafts((cur) => ({ ...cur, [id]: value }));
  }

  async function resolve(
    action: HumanActionCardModel,
    resolution: Record<string, unknown>,
    status?: Exclude<HumanActionStatus, 'pending'>,
  ): Promise<void> {
    if (!authToken) {
      setError('Not logged in');
      return;
    }
    setBusy(action.action_id);
    setError(null);
    const result = await resolveHumanActionRequest({
      backendUrl,
      authToken,
      actionId: action.action_id,
      resolution,
      status,
      fetchImpl,
    });
    setBusy(null);
    if (!result.ok) setError(result.error);
  }

  async function createAndResolve(
    kind: HumanActionKind,
    payload: Record<string, unknown>,
    resolution: Record<string, unknown>,
    status?: Exclude<HumanActionStatus, 'pending'>,
  ): Promise<void> {
    if (!authToken) {
      setError('Not logged in');
      return;
    }
    setBusy(`create:${kind}`);
    setError(null);
    const created = await createHumanActionRequest({
      backendUrl,
      authToken,
      sessionId,
      kind,
      payload,
      fetchImpl,
    });
    if (!created.ok) {
      setBusy(null);
      setError(created.error);
      return;
    }
    const resolved = await resolveHumanActionRequest({
      backendUrl,
      authToken,
      actionId: created.action_id,
      resolution,
      status,
      fetchImpl,
    });
    setBusy(null);
    if (!resolved.ok) setError(resolved.error);
  }

  async function submitQuickInject(e: FormEvent): Promise<void> {
    e.preventDefault();
    const value = quickInject.trim();
    if (!value) return;
    await createAndResolve(
      'inject',
      {
        prompt: value,
        selected_node_id: selectedNodeId ?? null,
        scope: 'current_run',
      },
      {
        constraint: value,
        selected_node_id: selectedNodeId ?? null,
        scope: 'current_run',
      },
    );
    setQuickInject('');
  }

  return (
    <section style={panelStyle} data-testid="human-action-panel">
      <div style={panelHeadStyle}>
        <span style={panelTitleStyle}>
          <Icon name="user" size={13} />
          人工介入
        </span>
        <span style={countStyle} data-testid="human-action-pending-count">
          {pending.length}
        </span>
      </div>
      {error && <div style={errorStyle} data-testid="human-action-error">{error}</div>}
      {pending.map((action) => (
        <div key={action.action_id} style={cardStyle} data-testid={`human-action-${action.kind}`}>
          <div style={cardHeadStyle}>
            <span style={kindPillStyle}>
              <Icon name={KIND_ICON[action.kind]} size={12} />
              {KIND_LABEL[action.kind]}
            </span>
            <span style={actionTitleStyle}>{actionTitle(action)}</span>
          </div>
          <ActionBody
            action={action}
            busy={busy === action.action_id}
            disabled={!canSubmit}
            draft={draft(action.action_id)}
            onDraft={(value) => setDraft(action.action_id, value)}
            previewOpen={previewOpen[action.action_id] === true}
            onTogglePreview={() => setPreviewOpen((cur) => ({ ...cur, [action.action_id]: !cur[action.action_id] }))}
            onResolve={(resolution, status) => { void resolve(action, resolution, status); }}
          />
        </div>
      ))}
      {authToken && (
        <div style={quickBoxStyle}>
          <form onSubmit={submitQuickInject} style={quickInjectStyle}>
            <textarea
              value={quickInject}
              onChange={(e) => setQuickInject(e.target.value)}
              placeholder="补充当前节点约束"
              rows={2}
              style={textareaStyle}
              data-testid="human-action-quick-inject"
            />
            <button
              type="submit"
              style={smallButtonStyle}
              disabled={busy === 'create:inject' || quickInject.trim().length === 0}
              title="插话"
              data-testid="human-action-submit-inject"
            >
              <Icon name="send" size={13} />
              插话
            </button>
          </form>
          <div style={takeoverRowStyle}>
            {(['manual_edit', 'rerun', 'branch', 'abandon'] as const).map((choice) => (
              <button
                key={choice}
                type="button"
                style={smallButtonStyle}
                disabled={busy === 'create:takeover'}
                data-testid={`human-action-takeover-${choice}`}
                onClick={() => {
                  void createAndResolve(
                    'takeover',
                    {
                      prompt: 'takeover',
                      selected_node_id: selectedNodeId ?? null,
                    },
                    {
                      choice,
                      selected_node_id: selectedNodeId ?? null,
                    },
                  );
                }}
              >
                {takeoverLabel(choice)}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ActionBody({
  action,
  busy,
  disabled,
  draft,
  onDraft,
  previewOpen,
  onTogglePreview,
  onResolve,
}: {
  action: HumanActionCardModel;
  busy: boolean;
  disabled: boolean;
  draft: string;
  onDraft: (value: string) => void;
  previewOpen: boolean;
  onTogglePreview: () => void;
  onResolve: (resolution: Record<string, unknown>, status?: Exclude<HumanActionStatus, 'pending'>) => void;
}): JSX.Element {
  if (action.kind === 'select') {
    const options = optionList(action.payload.options);
    return (
      <div style={buttonWrapStyle}>
        {options.length === 0 ? (
          <span style={mutedStyle}>暂无选项</span>
        ) : options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            style={actionButtonStyle}
            disabled={disabled || busy}
            onClick={() => onResolve({ selected_value: opt.value, selected_label: opt.label })}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (action.kind === 'approve') {
    const preview = textValue(action.payload.preview) ?? textValue(action.payload.args) ?? '';
    return (
      <div>
        {textValue(action.payload.reason) && (
          <div style={mutedStyle}>{textValue(action.payload.reason)}</div>
        )}
        {preview && previewOpen && <pre style={previewStyle}>{preview}</pre>}
        <div style={buttonWrapStyle}>
          {preview && (
            <button type="button" style={actionButtonStyle} onClick={onTogglePreview}>
              预览
            </button>
          )}
          <button
            type="button"
            style={actionButtonStyle}
            disabled={disabled || busy}
            onClick={() => onResolve({ decision: 'approve' })}
          >
            批准
          </button>
          <button
            type="button"
            style={dangerButtonStyle}
            disabled={disabled || busy}
            onClick={() => onResolve({ decision: 'reject' })}
          >
            拒绝
          </button>
        </div>
      </div>
    );
  }

  if (action.kind === 'takeover') {
    return (
      <div style={buttonWrapStyle}>
        {(['manual_edit', 'rerun', 'branch', 'abandon'] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            style={choice === 'abandon' ? dangerButtonStyle : actionButtonStyle}
            disabled={disabled || busy}
            onClick={() => onResolve({ choice })}
          >
            {takeoverLabel(choice)}
          </button>
        ))}
      </div>
    );
  }

  const placeholder =
    action.kind === 'fill'
      ? '填写回复'
      : action.kind === 'upload'
        ? 'artifact/context ref 或说明'
        : '补充约束';
  const submitLabel =
    action.kind === 'fill'
      ? '提交'
      : action.kind === 'upload'
        ? '提交材料'
        : '记录';

  return (
    <form
      style={stackStyle}
      onSubmit={(e) => {
        e.preventDefault();
        if (action.kind === 'upload') {
          onResolve({
            context_refs: draft.trim() ? [draft.trim()] : [],
            note: draft.trim(),
          });
        } else if (action.kind === 'inject') {
          onResolve({ constraint: draft.trim(), scope: 'current_run' });
        } else {
          onResolve({ value: draft.trim() });
        }
      }}
    >
      <textarea
        value={draft}
        onChange={(e) => onDraft(e.target.value)}
        rows={2}
        placeholder={placeholder}
        style={textareaStyle}
      />
      <div style={buttonWrapStyle}>
        <button
          type="submit"
          style={actionButtonStyle}
          disabled={disabled || busy || draft.trim().length === 0}
        >
          {submitLabel}
        </button>
        {action.kind === 'upload' && (
          <button
            type="button"
            style={dangerButtonStyle}
            disabled={disabled || busy}
            onClick={() => onResolve({ skipped: true, reason: draft.trim() }, 'canceled')}
          >
            跳过
          </button>
        )}
      </div>
    </form>
  );
}

function humanKind(value: unknown): HumanActionKind | null {
  return typeof value === 'string' && HUMAN_KINDS.has(value as HumanActionKind)
    ? value as HumanActionKind
    : null;
}

function humanStatus(value: unknown): HumanActionStatus | null {
  return typeof value === 'string' && HUMAN_STATUSES.has(value as HumanActionStatus)
    ? value as HumanActionStatus
    : null;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function textValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function actionTitle(action: HumanActionCardModel): string {
  const title =
    textValue(action.payload.title) ??
    textValue(action.payload.prompt) ??
    textValue(action.payload.tool_name) ??
    textValue(action.payload.question);
  return title ?? action.action_id.slice(0, 8);
}

function optionList(raw: unknown): Array<{ value: string; label: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ value: string; label: string }> = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      out.push({ value: item, label: item });
      continue;
    }
    const o = objectValue(item);
    const value = textValue(o.value) ?? textValue(o.id);
    const label = textValue(o.label) ?? value;
    if (value && label) out.push({ value, label });
  }
  return out.slice(0, 8);
}

function takeoverLabel(choice: 'manual_edit' | 'rerun' | 'branch' | 'abandon'): string {
  if (choice === 'manual_edit') return '手动编辑';
  if (choice === 'rerun') return '重跑';
  if (choice === 'branch') return '分支';
  return '放弃';
}

const panelStyle: CSSProperties = {
  borderBottom: '1.5px dashed var(--border-soft)',
  background: 'var(--bg-surface)',
  padding: '8px 10px',
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
  maxHeight: 250,
  overflowY: 'auto',
};

const panelHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontFamily: 'var(--font-body)',
  fontSize: 12,
};

const panelTitleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: 'var(--fg-default)',
  fontWeight: 700,
};

const countStyle: CSSProperties = {
  marginLeft: 'auto',
  minWidth: 22,
  height: 18,
  display: 'inline-grid',
  placeItems: 'center',
  border: '1.2px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-base)',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
};

const cardStyle: CSSProperties = {
  border: '1.2px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-base)',
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
};

const cardHeadStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
};

const kindPillStyle: CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  border: '1.2px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  padding: '2px 5px',
  fontSize: 11,
  fontWeight: 700,
};

const actionTitleStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 12,
  color: 'var(--fg-soft)',
};

const buttonWrapStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};

const actionButtonStyle: CSSProperties = {
  border: '1.2px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg-default)',
  padding: '4px 7px',
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};

const dangerButtonStyle: CSSProperties = {
  ...actionButtonStyle,
  color: 'var(--accent-danger)',
};

const smallButtonStyle: CSSProperties = {
  ...actionButtonStyle,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  padding: '4px 6px',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1.2px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  padding: 6,
  resize: 'vertical',
  minHeight: 44,
  background: 'var(--bg-base)',
  color: 'var(--fg-default)',
  fontFamily: 'var(--font-body)',
  fontSize: 12,
};

const stackStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const quickBoxStyle: CSSProperties = {
  borderTop: '1.2px dashed var(--border-soft)',
  paddingTop: 7,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
};

const quickInjectStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const takeoverRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
};

const mutedStyle: CSSProperties = {
  color: 'var(--fg-faint)',
  fontSize: 12,
};

const errorStyle: CSSProperties = {
  color: 'var(--accent-danger)',
  fontSize: 12,
  fontFamily: 'var(--font-body)',
};

const previewStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  maxHeight: 120,
  overflow: 'auto',
  border: '1.2px solid var(--border-soft)',
  borderRadius: 'var(--radius-sm)',
  padding: 6,
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  background: 'var(--bg-surface)',
};
